import type { AppResponseListChatSessionResponse, ChatSessionResponse } from "@agent-service-types/index.ts";
import type { UseQueryOptions } from "@tanstack/vue-query";
import { useQuery } from "@tanstack/vue-query";
import type { AxiosResponse } from "axios";
import type { MaybeRef } from "vue";
import { computed, toValue } from "vue";
import { agentServiceRequest } from "@/services/utils";

interface ChatSessionsParams {
  userOktaId?: string | null;
  externalChatSessionId?: string | null;
  title?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  textFeedback?: string | null;
  ratingFeedback?: number | null;
  isAnonymous?: boolean | null;
  searchString?: string | null;
  orderBy?: "title" | "first_name" | "last_name" | "email" | "text_feedback" | "rating_feedback" | "created_at" | null;
  orderDirection?: "asc" | "desc" | null;
  limit?: number;
  offset?: number;
}

export const getChatSessionsQueryKey = (params?: MaybeRef<ChatSessionsParams | undefined>) => {
  const p = toValue(params);
  return p !== undefined ? ["get-chat-sessions", p] : ["get-chat-sessions"];
};

export const useGetChatSessions = <TData = AppResponseListChatSessionResponse>(
  params?: MaybeRef<ChatSessionsParams | undefined>,
  useQueryOptions?: Partial<UseQueryOptions<AppResponseListChatSessionResponse, Error, TData>>,
) => {
  const queryKey = computed(() => getChatSessionsQueryKey(params));

  return useQuery<AppResponseListChatSessionResponse, Error, TData>({
    queryKey,
    queryFn: async (): Promise<AppResponseListChatSessionResponse> => {
      const p = toValue(params);
      const response: AxiosResponse<AppResponseListChatSessionResponse> = await agentServiceRequest({
        method: "get",
        url: "/chat_sessions/",
        params: {
          user_okta_id: p?.userOktaId,
          external_chat_session_id: p?.externalChatSessionId,
          title: p?.title,
          first_name: p?.firstName,
          last_name: p?.lastName,
          email: p?.email,
          text_feedback: p?.textFeedback,
          rating_feedback: p?.ratingFeedback,
          is_anonymous: typeof p?.isAnonymous === "boolean" ? `${p?.isAnonymous}` : undefined,
          search_string: p?.searchString,
          order_by: p?.orderBy,
          order_direction: p?.orderDirection,
          limit: p?.limit,
          offset: p?.offset,
        },
      });
      return response.data;
    },
    ...useQueryOptions,
  });
};
